import { DatePicker, Dropdown, IDropdownOption, Label, Panel, PanelType, PrimaryButton, Stack } from "office-ui-fabric-react";
import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom"
import { ProfileContext } from "../../../Home";
import { ConstraintType } from "../../../types/Booking.types";
import Utils from "../../../utils";

export interface IBookingDatePanelProps {
    onDismiss: () => void;
}

const getNearestTime = () => {
    const date = new Date();
    let mins = Math.round(date.getMinutes() / 15) * 15;
    let hours = date.getHours();
    if (mins >= 60) {
        mins = 0;
        hours++;
    }
    return { hours, mins };
}

const today = new Date();
today.setSeconds(0); today.setMilliseconds(0);
let { hours: initialStartHours, mins: initialMins } = getNearestTime();
let initialEndHours = initialStartHours + 3; 
if (initialEndHours >= 23) {
    initialEndHours = 23;
}

export const BookingDatePanel = (props: IBookingDatePanelProps) => {

    const profileContext = useContext(ProfileContext);

    const { onDismiss } = props;

    const history = useHistory();

    const [ date, setDate ] = useState<Date|undefined>(today);
    const [ startHours, setStartHours ] = useState<number|undefined>(initialStartHours);
    const [ startMins, setStartMins ] = useState<number|undefined>(initialMins);
    const [ endHours, setEndHours ] = useState<number|undefined>(initialEndHours);
    const [ endMins, setEndMins ] = useState<number|undefined>(initialMins);

    // If no dates in state, initialise as current time to cur time + 30 mins
    useEffect(() => {
        if (startHours !== undefined && startMins !== undefined && 
            endHours !== undefined && endMins !== undefined) {
            const dateA = new Date();
            dateA.setHours(startHours); dateA.setMinutes(startMins);
            const dateB = new Date(dateA.getTime());
            dateB.setHours(endHours); dateB.setMinutes(endMins);
            let nextDay = false;
            let endTimeChanged = false;
            if (dateA > dateB) {
                endTimeChanged = true;
                while (dateA >= dateB) {
                    dateB.setMinutes(dateB.getMinutes() + 30);
                    if (dateB.getDate() > dateA.getDate()) {
                        dateA.setTime(dateB.getTime());
                        dateB.setMinutes(dateB.getMinutes() + 30);
                        nextDay = true;
                    }
                }
            }
            if (endTimeChanged) {
                setStartHours(dateA.getHours());
                setStartMins(dateA.getMinutes());
                setEndHours(dateB.getHours());
                setEndMins(dateB.getMinutes());
            }
            if (nextDay) {
                setDate(dateA);
            }
        }
    }, [startHours, startMins, endHours, endMins]);

    // If changed date / time is less than current date / time, update to now
    useEffect(() => {
        if (date && date.toLocaleDateString() === new Date().toLocaleDateString()) {
            const currentTime = new Date();
            if (startHours && startHours < currentTime.getHours()) {
                setStartHours(currentTime.getHours());
            }
        }
    }, [date, startHours]);

    // Dropdown options for hours and minutes
    let hourOptions: IDropdownOption[] = [], minOptions: IDropdownOption[] = [], startHourOption = 8;
    if (date?.toLocaleDateString() === new Date().toLocaleDateString()) {
        startHourOption = new Date().getHours();
    }
    for (let i = startHourOption; i < 24; i++) {
        const dropdownOption = {
            key: i,
            text: ("0" + i).slice(-2)
        } as IDropdownOption;
        hourOptions.push(dropdownOption);
    }
    for (let i = 0; i < 60; i += 15) {
        const dropdownOption = {
            key: i,
            text: ("0" + i).slice(-2)
        } as IDropdownOption;
        minOptions.push(dropdownOption);
    } 

    // If Desk Booking Time Constraint set globally, set maximum date on the date picker
    let maxDate: Date | undefined;
    if (profileContext?.Settings.GlobalDeskConstraint != null) {
        const num = profileContext.Settings.GlobalDeskConstraint.Number;
        const type = profileContext.Settings.GlobalDeskConstraint.Type as ConstraintType;
        if (num) {
            maxDate = new Date();
            switch (type) {
                case ConstraintType.Week:
                    maxDate.setDate(maxDate.getDate() + (7 * num));
                    break;
                case ConstraintType.Month:
                    maxDate.setMonth(maxDate.getMonth() + num);
                    break;
                case ConstraintType.Year:
                    maxDate.setFullYear(maxDate.getFullYear() + num);
                    break;
            }
        }
    }

    return (
        <Panel type={PanelType.smallFixedFar} headerText="Choose a date" isOpen={true} onDismiss={onDismiss}>
            <DatePicker 
                style={{marginBottom: 20}} 
                label="Date" 
                minDate={today} 
                maxDate={maxDate}
                value={date} 
                formatDate={(date) => date ? date.toLocaleDateString() : ""} 
                onSelectDate={(newDate) => setDate(newDate || undefined)} 
            />
            <Label>Start Time</Label>
            <Stack horizontal horizontalAlign="center" verticalAlign="center">
                <Dropdown options={hourOptions} selectedKey={startHours} onChange={(e, option) => {
                    if (option) setStartHours(option.key as number);
                }} />
                <Dropdown options={minOptions} selectedKey={startMins} onChange={(e, option) => {
                    if (option) setStartMins(option.key as number);
                }} />
            </Stack>
            <Label>End Time</Label>
            <Stack horizontal horizontalAlign="center" verticalAlign="center" style={{marginBottom: 40}}>
                <Dropdown options={hourOptions} selectedKey={endHours} onChange={(e, option) => {
                    if (option) setEndHours(option.key as number);
                }} />
                <Dropdown options={minOptions} selectedKey={endMins} onChange={(e, option) => {
                    if (option) setEndMins(option.key as number);
                }}/>
            </Stack>
            <Stack horizontal horizontalAlign="end" verticalAlign="center">
                <PrimaryButton text="Next" onClick={() => {
                    if (
                        date && 
                        startHours !== undefined && startMins !== undefined && 
                        endHours !== undefined && endMins !== undefined
                    ) {
                        const dateA = new Date(date.getTime());
                        const dateB = new Date(date.getTime());
                        dateA.setHours(startHours); dateA.setMinutes(startMins);
                        dateB.setHours(endHours); dateB.setMinutes(endMins);
                        history.push(`/menu/book/desk/${encodeURIComponent(Utils.dateToLocalISO(dateA))}/${encodeURIComponent(Utils.dateToLocalISO(dateB))}`);
                    }
                }} />
            </Stack>
        </Panel>
    )
}